import {
  ActionIcon,
  Box,
  Button,
  Group,
  Modal,
  Paper,
  Stack,
  Text,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconBan,
  IconGripVertical,
  IconLogout,
  IconUserMinus,
  IconUserPlus,
  IconX,
} from "@tabler/icons-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { AvatarState } from "../../shared/types";
import { AvatarPreviewCanvas } from "./AvatarPreviewCanvas";
import { DROBBIT_WAVE_DURATION_MS } from "../lib/drobbit-model";

const PANEL_WIDTH = 260;
const PANEL_HEIGHT = 340;

function clampPosition(x: number, y: number): { x: number; y: number } {
  const margin = 12;
  const maxX = Math.max(margin, window.innerWidth - PANEL_WIDTH - margin);
  const maxY = Math.max(margin, window.innerHeight - PANEL_HEIGHT - margin);
  return {
    x: Math.min(Math.max(margin, x), maxX),
    y: Math.min(Math.max(margin, y), maxY),
  };
}

function initialPosition(): { x: number; y: number } {
  if (typeof window === "undefined") return { x: 16, y: 200 };
  return clampPosition(16, window.innerHeight - PANEL_HEIGHT - 80);
}

interface Props {
  profile: AvatarState;
  friendStatus: "accepted" | "pending" | null | undefined;
  canAcceptFriendRequest: boolean;
  acceptingFriendRequest: boolean;
  onAcceptFriendRequest: () => void;
  canSendFriendRequest: boolean;
  sendingFriendRequest: boolean;
  onSendFriendRequest: () => void;
  canReport: boolean;
  sendingReport: boolean;
  onReport: () => void;
  canKick: boolean;
  onKick: () => void;
  canBlock: boolean;
  onBlock: () => void;
  canRemoveFriend: boolean;
  onRemoveFriend: () => void;
  onClose: () => void;
}

export function PlayerProfileCard({
  profile,
  friendStatus,
  canAcceptFriendRequest,
  acceptingFriendRequest,
  onAcceptFriendRequest,
  canSendFriendRequest,
  sendingFriendRequest,
  onSendFriendRequest,
  canReport,
  sendingReport,
  onReport,
  canKick,
  onKick,
  canBlock,
  onBlock,
  canRemoveFriend,
  onRemoveFriend,
  onClose,
}: Props) {
  const [position, setPosition] = useState(initialPosition);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const dragState = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const stopDragging = useCallback(() => {
    dragState.current = null;
    document.body.style.userSelect = "";
  }, []);

  const onDragMove = useCallback((event: PointerEvent) => {
    const state = dragState.current;
    if (!state) return;
    const dx = event.clientX - state.startX;
    const dy = event.clientY - state.startY;
    setPosition(clampPosition(state.originX + dx, state.originY + dy));
  }, []);

  const onDragEnd = useCallback(
    function handleDragEnd() {
      stopDragging();
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", handleDragEnd);
      window.removeEventListener("pointercancel", handleDragEnd);
    },
    [onDragMove, stopDragging]
  );

  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev.x, prev.y));
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("pointermove", onDragMove);
      window.removeEventListener("pointerup", onDragEnd);
      window.removeEventListener("pointercancel", onDragEnd);
      stopDragging();
    };
  }, [onDragEnd, onDragMove, stopDragging]);

  const handleHeaderPointerDown = (
    event: ReactPointerEvent<HTMLDivElement>
  ) => {
    if (event.button !== 0) return;
    event.preventDefault();
    dragState.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
    };
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", onDragMove);
    window.addEventListener("pointerup", onDragEnd);
    window.addEventListener("pointercancel", onDragEnd);
  };

  return (
    <Paper
      p="sm"
      radius="md"
      style={{
        position: "absolute",
        top: position.y,
        left: position.x,
        width: PANEL_WIDTH,
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        background: "#ffffff",
        border: "1px solid rgba(0,0,0,0.10)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        pointerEvents: "auto",
      }}
    >
      <Box
        onPointerDown={handleHeaderPointerDown}
        style={{
          touchAction: "none",
          cursor: "grab",
          marginBottom: 4,
          userSelect: "none",
        }}
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            <IconGripVertical size={14} color="rgba(0,0,0,0.3)" />
            <Text fw={700} size="sm" c="dark" truncate>
              {profile.name}
            </Text>
          </Group>
          <ActionIcon variant="subtle" size="sm" color="gray" onClick={onClose}>
            <IconX size={14} />
          </ActionIcon>
        </Group>
      </Box>

      <Text size="xs" c="gray.6" ff="monospace" mb="xs" px={2}>
        {profile.address.slice(0, 8)}...{profile.address.slice(-6)}
      </Text>

      <Box
        mb="sm"
        style={{
          height: 150,
          borderRadius: 10,
          overflow: "hidden",
          border: "1px solid rgba(0,0,0,0.08)",
          background:
            "radial-gradient(ellipse at 50% 80%, rgba(253,126,20,0.04) 0%, transparent 60%), linear-gradient(180deg, #edf2f7 0%, #f8f9fa 100%)",
        }}
      >
        <AvatarPreviewCanvas
          color={profile.color || "#000000"}
          interactive={false}
          animationMode={
            typeof profile.waveAt === "number" &&
            Date.now() - profile.waveAt <= DROBBIT_WAVE_DURATION_MS
              ? "wave"
              : profile.isDancing
              ? "dance"
              : "idle"
          }
        />
      </Box>

      <Modal
        opened={confirmRemoveOpen}
        onClose={() => setConfirmRemoveOpen(false)}
        title="Remove Friend"
        centered
        size="xs"
      >
        <Text size="sm" c="dark" mb="md">
          Are you sure you want to remove <b>{profile.name}</b> as a friend?
        </Text>
        <Group justify="flex-end" gap="xs">
          <Button
            size="xs"
            variant="default"
            onClick={() => setConfirmRemoveOpen(false)}
          >
            Cancel
          </Button>
          <Button
            size="xs"
            color="red"
            leftSection={<IconUserMinus size={14} />}
            onClick={() => {
              setConfirmRemoveOpen(false);
              onRemoveFriend();
            }}
          >
            Remove
          </Button>
        </Group>
      </Modal>

      <Stack gap={6}>
        {friendStatus === "accepted" ? (
          <Button
            size="xs"
            color="red"
            variant="light"
            leftSection={<IconUserMinus size={14} />}
            onClick={() => setConfirmRemoveOpen(true)}
          >
            Remove Friend
          </Button>
        ) : canAcceptFriendRequest ? (
          <Button
            size="xs"
            leftSection={<IconUserPlus size={14} />}
            loading={acceptingFriendRequest}
            onClick={onAcceptFriendRequest}
          >
            Accept Friend Request
          </Button>
        ) : (
          <Button
            size="xs"
            leftSection={<IconUserPlus size={14} />}
            loading={sendingFriendRequest}
            disabled={!canSendFriendRequest}
            onClick={onSendFriendRequest}
          >
            {friendStatus === "pending" ? "Request Pending" : "Add Friend"}
          </Button>
        )}
        <Button
          size="xs"
          color="red"
          variant="light"
          leftSection={<IconAlertTriangle size={14} />}
          loading={sendingReport}
          disabled={!canReport}
          onClick={onReport}
        >
          Report
        </Button>
        {canKick && (
          <Button
            size="xs"
            color="orange"
            variant="light"
            leftSection={<IconLogout size={14} />}
            onClick={onKick}
          >
            Kick From Room
          </Button>
        )}
        {canBlock && (
          <Button
            size="xs"
            color="red"
            variant="light"
            leftSection={<IconBan size={14} />}
            onClick={onBlock}
          >
            Block From Room
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
